/* eslint-disable @typescript-eslint/no-misused-promises */
import AutoSaveModal from '@gp/components/AutoSaveModal';
import Loading from '@gp/components/Loading';
import {
  DBG_DETAILS_CUR_MILESTONE_ID,
  FILE_TYPES_MAP,
} from '@gp/constants/const';
import { ALL_REPORT_OPERATION_LOG } from '@gp/constants/operationLogParams';
import {
  EDIT_PAGE_KEY_MAP,
  useAutoSaveParams,
} from '@gp/hooks/useAutoSaveParams';
import useDownload, { createDownloadService } from '@gp/hooks/useDownload';
import type { IMilestoneOperationVisibleState } from '@gp/models/milestoneOperationVisible';
import { MILESTONE_OPERATION_VISIBLE_MODEL_NAMESPACE } from '@gp/models/milestoneOperationVisible';
import type { IAddMilestoneFormModel } from '@gp/pages/Pipeline/ProjectDetails/Milestone/components/AddMilestoneModal';
import AddMilestoneModal from '@gp/pages/Pipeline/ProjectDetails/Milestone/components/AddMilestoneModal';
import MeetingModal from '@gp/pages/Pipeline/ProjectDetails/Milestone/components/MeetingModal';
import MilestoneCard from '@gp/pages/Pipeline/ProjectDetails/Milestone/components/MilestoneCard';
import MilestoneNoData from '@gp/pages/Pipeline/ProjectDetails/Milestone/components/NoData/Milestone';
import Topbar from '@gp/pages/Pipeline/ProjectDetails/Milestone/components/Topbar';
import type {
  IUpdateGRFormModel,
  IUpdateGRModalInstance,
} from '@gp/pages/Pipeline/ProjectDetails/Milestone/components/UpdateGRModal';
import UpdateGRModal from '@gp/pages/Pipeline/ProjectDetails/Milestone/components/UpdateGRModal';
import { getSeparatedAndUniqueMilestones } from '@gp/pages/Pipeline/ProjectDetails/Milestone/components/UpdateGRModal/FinancialsFormControl/utils';
import '@gp/pages/Pipeline/ProjectDetails/Milestone/index.scss';
import MilestoneService from '@gp/pages/Pipeline/ProjectDetails/Milestone/services';
import type {
  IMilestone,
  IMilestoneMap,
} from '@gp/pages/Pipeline/ProjectDetails/Milestone/types';
import {
  formatResMilestones,
  getKeyMilestones,
  getMilestoneMap,
} from '@gp/pages/Pipeline/ProjectDetails/Milestone/utils';
import { reportOperationLog } from '@gp/services/global';
import { milestoneListDownLoad } from '@gp/services/projectDetail';
import { reportBtnOperationLog } from '@gp/utils/common';
import type { IBaseProps } from '@home/types/common';
import { useToggle, useDeepCompareEffect, useSize } from 'ahooks';
import classnames from 'classnames';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'umi';
import useMilestoneAutoScroll from './_hooks/useMilestoneAutoScroll';

interface IMilestoneProps extends IBaseProps {
  pageContainer?: React.MutableRefObject<HTMLElement | null>;
  userIsAbleEdit?: boolean;
  editable?: boolean;
  detailInfoAuthorityData?: Record<string, boolean>;
  // 当前Project详情
  projectDetailData?: Record<string, any>;
  // 当前Project的Milestones集合加载状态
  loading?: boolean;
  // 当前Project的Milestones集合更新日期
  updatedDate?: string;
  // 自动定位，并展开的milestone的id
  milestoneId?: string;
  // 当前Project的Milestones集合
  milestones?: IMilestone[];
  // 刷新Project详情数据，重新调接口查询数据
  onRefreshProjectDetails?: () => void;
  // 刷新Milestones集合数据，重新调接口查询数据
  onRefreshMilestones?: () => void;
}

const Milestone: React.FC<IMilestoneProps> = (props) => {
  const {
    style,
    className,
    pageContainer,
    userIsAbleEdit,
    editable,
    detailInfoAuthorityData,
    projectDetailData,
    loading = false,
    updatedDate,
    milestoneId: milestoneIdProps,
    milestones: milestonesProps,
    onRefreshProjectDetails,
    onRefreshMilestones,
    addMilestoneVisible,
    setAddMilestoneVisible,
    milestoneCategory,
    actionItemCategory,
    isAdd,
    setIsAdd,
  } = props;

  const dispatch = useDispatch();
  const { width } = useSize(document.body) || {};
  const { visible: milestoneOperationVisible = false } = useSelector<
    any,
    IMilestoneOperationVisibleState
  >(
    (rootState) =>
      rootState?.[MILESTONE_OPERATION_VISIBLE_MODEL_NAMESPACE] || {},
  );

  const { isDownloading, handleDownload } = useDownload({
    fileName: '',
    csvDownloadLogKey: '',
    pdfDownloadLogKey: '',
    downloadService: createDownloadService(milestoneListDownLoad),
  });

  const handleDownloadInPage = () => {
    handleDownload({
      fileType: FILE_TYPES_MAP.CSV,
      id: projectDetailData?.project_id,
    });
    // @ts-ignore
    reportOperationLog(
      ALL_REPORT_OPERATION_LOG.DETAIL_MILESTONE_INFO_CSV_DOWNLOAD_SUBMIT,
      'button',
    );
  };

  const milestoneListNode = useRef<HTMLDivElement>(null);
  const updateGRModal = useRef<IUpdateGRModalInstance>(null);
  const milestones = useMemo<IMilestone[]>(
    () => formatResMilestones(milestonesProps),
    [milestonesProps],
  );

  const milestoneMap = useMemo<IMilestoneMap>(
    () => getMilestoneMap(milestones),
    [milestones],
  );

  const separatedMilestones = useMemo<IMilestone[]>(
    () => getSeparatedAndUniqueMilestones(milestones),
    [milestones],
  );

  // Milestones集合是否无数据
  const noData = !Array.isArray(milestones) || !milestones.length;

  // 是否全部展示Milestones及其子Milestones
  const [allExpand, { toggle: toggleAllExpand }] = useToggle(false);
  const handleToggleAllExpand = () => {
    const logKey = !allExpand
      ? 'DETAIL_MILESTONE_INFO_EXPAND_ALL_MILESTONES'
      : 'DETAIL_MILESTONE_INFO_FOLD_ALL_MILESTONES';
    // @ts-ignore
    reportOperationLog(ALL_REPORT_OPERATION_LOG[logKey], 'button');
    toggleAllExpand();
  };

  // 当前Project的Current Milestone、Next Milestone
  const { currentMilestone, nextMilestone } = useMemo<{
    currentMilestone: IMilestone | null;
    nextMilestone: IMilestone | null;
  }>(() => {
    if (loading) return { currentMilestone: null, nextMilestone: null };
    return getKeyMilestones(
      milestones,
      projectDetailData?.current_milestone_id,
      projectDetailData?.next_milestone_id,
    );
  }, [
    loading,
    milestones,
    projectDetailData?.current_milestone_id,
    projectDetailData?.next_milestone_id,
  ]);

  // 当前操作的Milestone的id
  const [activeMilestoneId, setActiveMilestoneId] = useState('');
  // 当前操作的Milestone
  const activeMilestone = useMemo<IMilestone>(() => {
    return milestoneMap?.[activeMilestoneId];
  }, [milestoneMap, activeMilestoneId]);
  // 新增Milestone弹窗
  const [addMeetingVisible, setAddMeetingVisible] = useState(false);
  const [autoSaveVisible, setAutoSaveVisible] = useState(false);
  const [isUseSaveDatas, setIsUseSaveDatas] = useState(false);
  const [isClickMilestoneId, setIsClickMilestoneId] = useState(false);
  const [targetMilestoneId, setTargetMilestoneId] = useState('');

  const localMilestoneId = localStorage.getItem(DBG_DETAILS_CUR_MILESTONE_ID);

  const handleAddMilestone = () => {
    if (!editable) return;
    setActiveMilestoneId('');
    setAddMilestoneVisible(true);
    setIsAdd(true);
  };

  const goToUpdatedMilestone = (mileId: string) => {
    setIsClickMilestoneId(!isClickMilestoneId);
    setTargetMilestoneId(mileId);
  };

  const handleAddMilestoneConfirm = async (
    payload: IAddMilestoneFormModel,
    isAdd: boolean,
  ) => {
    const nextMilestoneId = nextMilestone?.milestone_id || '';
    try {
      if (isAdd) {
        await MilestoneService.addMilestone({
          studioId: projectDetailData?.studio_entity_id,
          projectId: projectDetailData?.project_id,
          projectName: projectDetailData?.project,
          milestones,
          formModel: payload,
          nextMilestoneId,
        });
      } else {
        await MilestoneService.updateMilestones({
          studioId: projectDetailData?.studio_entity_id,
          projectId: projectDetailData?.project_id,
          projectName: projectDetailData?.project,
          milestoneId: activeMilestoneId,
          milestoneMap,
          milestones,
          formModel: payload,
          nextMilestoneId,
        });
      }
      onRefreshProjectDetails?.();
      onRefreshMilestones?.();
      setActiveMilestoneId('');
      setAddMilestoneVisible(false);
      if (isAdd) {
        // @ts-ignore
        reportOperationLog(
          ALL_REPORT_OPERATION_LOG.DETAIL_MILESTONE_ADD_SUBMIT,
          'button',
        );
      } else {
        // @ts-ignore
        reportOperationLog(
          ALL_REPORT_OPERATION_LOG.DETAIL_MILESTONE_EDIT_SUBMIT,
          'button',
        );
      }
    } catch (exception) {
    } finally {
    }
  };

  const handleAddMeetingConfirm = async () => {};
  const handleAddMeetingCancel = async () => {
    setAddMeetingVisible(false);
  };

  const handleAddMilestoneCancel = () => {
    setActiveMilestoneId('');
    setAddMilestoneVisible(false);
  };

  const pageKeyCur = useMemo(() => {
    return `${EDIT_PAGE_KEY_MAP.pipeline_gameDetails_milestone_editGR}_${projectDetailData?.project_id}_${activeMilestoneId}`;
  }, [projectDetailData, activeMilestoneId]);

  // 自动保存hooks
  const { getSaveParamsValue, showRestorePrompt, deleteSaveParamsByPageKey } =
    useAutoSaveParams({
      pageKey: EDIT_PAGE_KEY_MAP.pipeline_gameDetails_gameInformation,
    });

  const handleUpdateMilestone = (payload?: string) => {
    if (!payload) return;
    setActiveMilestoneId(payload);
    setAddMeetingVisible(true);
    // setAddMilestoneVisible(true);
    setIsAdd(false);
  };

  // update GR弹窗是否显示
  const [updateGRVisible, setUpdateGRVisible] = useState(false);

  const setUpdateGRFormModel = (
    payload: string,
    milestoneMapTemp = milestoneMap,
  ) => {
    const activeMilestoneTmp = milestoneMapTemp?.[payload];
    updateGRModal.current?.init(
      {
        gate_review: activeMilestoneTmp?.gate_review || '',
        gate_review_decision: activeMilestoneTmp?.gate_review_decision || '',
        dev_cost_actual: activeMilestoneTmp?.dev_cost_actual || '',
        budget_next_milestone: activeMilestoneTmp?.budget_next_milestone || '',
        approved_budget_to_next:
          activeMilestoneTmp?.approved_budget_to_next || '',
        dev_cost: activeMilestoneTmp?.dev_cost || '',
        key_milestones_financial:
          activeMilestoneTmp?.key_milestones_financial || [],
        key_milestones_financial_forecast:
          activeMilestoneTmp?.key_milestones_financial_forecast || [],
        key_feedback: activeMilestoneTmp?.key_feedback || '',
        action_items: activeMilestoneTmp?.action_items || [],
        next_milestone_scope: activeMilestoneTmp?.next_milestone_scope || '',
        material: activeMilestoneTmp?.material || [],
      },
      activeMilestoneTmp?.date,
    );
  };

  const handleUpdateGR = (payload?: string) => {
    if (!payload) return;
    setActiveMilestoneId(payload);
    // 如果有自动保存过的，先咨询
    if (
      showRestorePrompt(
        `${EDIT_PAGE_KEY_MAP.pipeline_gameDetails_milestone_editGR}_${projectDetailData?.project_id}_${payload}`,
      )
    ) {
      setAutoSaveVisible(true);
    } else {
      setUpdateGRFormModel(payload);
      setUpdateGRVisible(true);
    }
  };

  const handleAutoSaveConfirm = () => {
    const newValue = getSaveParamsValue(pageKeyCur) || {};
    const milestoneTemp = {
      ...activeMilestone,
      ...newValue,
    };
    const milestoneMapTemp = {
      ...milestoneMap,
      [activeMilestoneId]: milestoneTemp,
    };

    setUpdateGRFormModel(activeMilestoneId, milestoneMapTemp);
    setAutoSaveVisible(false);
    setUpdateGRVisible(true);
    setIsUseSaveDatas(true);
  };
  const handleAutoSaveCancel = () => {
    setAutoSaveVisible(false);
    deleteSaveParamsByPageKey(pageKeyCur);
    setUpdateGRFormModel(activeMilestoneId, milestoneMap);
    setUpdateGRVisible(true);
    setIsUseSaveDatas(false);
  };

  const handleUpdateGRConfirm = async (payload?: IUpdateGRFormModel) => {
    try {
      const nextMilestoneId = nextMilestone?.milestone_id || '';
      await MilestoneService.updateMilestoneGR({
        studioId: projectDetailData?.studio_entity_id,
        projectId: projectDetailData?.project_id,
        projectName: projectDetailData?.project,
        milestoneId: activeMilestoneId,
        milestoneMap,
        milestones,
        formModel: payload,
        nextMilestoneId,
      });
      onRefreshProjectDetails?.();
      onRefreshMilestones?.();
      setActiveMilestoneId('');
      deleteSaveParamsByPageKey(pageKeyCur);
      setUpdateGRVisible(false);
      setIsUseSaveDatas(false);
      // @ts-ignore
      reportOperationLog(
        ALL_REPORT_OPERATION_LOG.DETAIL_MILESTONE_EDIT_SUBMIT,
        'button',
      );
    } catch (exception) {
    } finally {
    }
  };

  const handleUpdateGRCancel = () => {
    deleteSaveParamsByPageKey(pageKeyCur);
    setActiveMilestoneId('');
    setUpdateGRVisible(false);
    setIsUseSaveDatas(false);
  };

  const handleDeleteMilestone = async (payload?: string) => {
    if (!payload) return;
    try {
      const nextMilestoneId = nextMilestone?.milestone_id || '';
      await MilestoneService.deleteMilestone({
        studioId: projectDetailData?.studio_entity_id,
        projectId: projectDetailData?.project_id,
        projectName: projectDetailData?.project,
        milestoneId: payload,
        milestoneMap,
        milestones,
        nextMilestoneId,
      });
      onRefreshProjectDetails?.();
      onRefreshMilestones?.();
      // @ts-ignore
      reportOperationLog(
        ALL_REPORT_OPERATION_LOG.DETAIL_MILESTONE_DELETE_SUBMIT,
        'button',
      );
    } catch (exception) {
    } finally {
    }
  };

  useEffect(() => {
    // @ts-ignore
    reportOperationLog({
      ...ALL_REPORT_OPERATION_LOG.PIPELINE_DETAIL_MILESTONE,
      uidType: 'game_code',
      uid: projectDetailData?.project_id || '',
      extInfo: projectDetailData?.project || '',
    });
  }, []);

  useDeepCompareEffect(() => {
    if (milestones && Array.isArray(milestones)) {
      if (
        localMilestoneId &&
        milestones.some((m) => m.milestone_id === localMilestoneId)
      ) {
        setTargetMilestoneId(localMilestoneId);
      }
    }
  }, [loading, noData, milestones, localMilestoneId]);

  const milestoneAutoScrollDeps = useMemo(
    () => [loading, noData, milestones, targetMilestoneId],
    [loading, noData, milestones, targetMilestoneId],
  );

  useMilestoneAutoScroll(
    pageContainer,
    milestoneListNode,
    {
      milestoneId: targetMilestoneId || undefined,
      milestones,
      deps: milestoneAutoScrollDeps,
    },
    isClickMilestoneId,
  );

  // 组件卸载时清队当前定位的里程碑ID
  useEffect(() => {
    return () => {
      localStorage.setItem(DBG_DETAILS_CUR_MILESTONE_ID, '');
      setTargetMilestoneId('');
    };
  }, []);

  return (
    <div
      style={style}
      className={classnames('dbg-project-details-milestone', className, {
        'dbg-project-details-milestone-small':
          width !== undefined && width <= 1480,
      })}
    >
      <Topbar
        userIsAbleEdit={userIsAbleEdit}
        editable={editable}
        isDownloading={isDownloading}
        // 是否仅展示添加Production Milestone按钮
        onlyShowAddBtn={loading || noData}
        projectId={projectDetailData?.project_id}
        studioId={projectDetailData?.studio_entity_id}
        stage={projectDetailData?.stage}
        // 当前project下的Milestone集合
        milestones={milestones}
        // 当前project的Next Milestone
        nextMilestone={nextMilestone}
        // updated日期
        updatedDate={updatedDate}
        // 是否全部展开Production Milestones及其Sub Milestones
        allExpand={allExpand}
        // 切换是否全部展开Production Milestones及其Sub Milestones
        toggleAllExpand={handleToggleAllExpand}
        milestoneOperationVisible={milestoneOperationVisible}
        // 添加Production Milestone的回调
        onAdd={handleAddMilestone}
        goToUpdatedMilestone={goToUpdatedMilestone}
        // 下载的回调
        onDownload={handleDownloadInPage}
        // 更新Next Milestone的回调
        onUpdateNextMilestoneSuccess={() => {
          onRefreshProjectDetails?.();
          reportBtnOperationLog(
            'DETAIL_MILESTONE_INFO_SETTING_NEXT_MILESTONE_SUBMIT',
          );
        }}
        onUpdateMilestoneOperationVisible={(payload) => {
          dispatch({
            type: `${MILESTONE_OPERATION_VISIBLE_MODEL_NAMESPACE}/updateMilestoneOperationVisible`,
            payload,
          });
        }}
      />
      {loading && (
        <Loading
          loading
          style={{ height: 'calc(100vh - 400px)' }}
          // loadingBg="#f2f3f5"
        />
      )}
      {/* Milestones集合无数据 */}
      {!loading && noData && (
        <MilestoneNoData
          editable={editable}
          onAddMilestone={handleAddMilestone}
        />
      )}
      {/* Milestones集合有数据 */}
      {!loading && !noData && (
        <>
          <div
            ref={milestoneListNode}
            className="dbg-milestone-context dbg-milestone-context-sticky"
          >
            {milestones.map((milestone) => {
              const localMilestoneId = localStorage.getItem(
                DBG_DETAILS_CUR_MILESTONE_ID,
              );
              const milestoneId = milestone?.milestone_id;
              const isDefaultMilestone = milestoneId === milestoneIdProps;
              const isCurrentMilestone =
                milestoneId === currentMilestone?.milestone_id;
              const isNextMilestone =
                milestoneId === nextMilestone?.milestone_id;
              /**
               * 当前milestone是否展开
               * 1、有指定默认展开milestone的id，并且当前milestone就是指定默认展开的milestone
               * 2、没有指定默认展开milestone的id，并且当前milestone是currentMilestone或者nextMilestone
               */
              const isExpand =
                isDefaultMilestone ||
                (!milestoneIdProps && (isCurrentMilestone || isNextMilestone));
              // const isLocalMilestoneId =
              //   localMilestoneId && milestoneId === localMilestoneId;
              const isLocalMilestoneId =
                localMilestoneId && milestoneId === localMilestoneId;
              const isTargetMilestoneId =
                targetMilestoneId && milestoneId === targetMilestoneId;
              let isExpandCur = false;
              if (localMilestoneId) {
                isExpandCur = !!isLocalMilestoneId;
              } else if (targetMilestoneId) {
                isExpandCur = !!isTargetMilestoneId;
              } else {
                isExpandCur = isExpand;
              }
              return (
                <MilestoneCard
                  key={milestoneId}
                  editable={editable}
                  milestoneOperationVisible={milestoneOperationVisible}
                  detailInfoAuthorityData={detailInfoAuthorityData}
                  projectDetailData={projectDetailData}
                  isNextMilestone={isNextMilestone}
                  isExpand={isExpandCur}
                  allExpand={allExpand}
                  // 注意这里是当前milestone的date，不是project的currentMilestone的date
                  currentMilestoneDate={milestone?.date}
                  separatedMilestones={separatedMilestones}
                  milestoneId={milestoneIdProps}
                  milestone={milestone}
                  onUpdateMilestone={handleUpdateMilestone}
                  onUpdateGR={handleUpdateGR}
                  onDeleteMilestone={handleDeleteMilestone}
                />
              );
            })}
          </div>
        </>
      )}
      {/* New Production Milestone弹窗、New Sub-Milestone弹窗 */}
      <AddMilestoneModal
        className="dbg-milestone-add-edit-modal"
        milestones={milestones}
        visible={addMilestoneVisible}
        setVisible={setAddMilestoneVisible}
        onConfirm={handleAddMilestoneConfirm}
        onCancel={handleAddMilestoneCancel}
        milestoneCategory={milestoneCategory}
        milestone={activeMilestone}
        milestoneMap={milestoneMap}
        isAdd={isAdd}
      />
      <MeetingModal
        className="dbg-milestone-add-edit-modal"
        milestones={milestones}
        visible={addMeetingVisible}
        setVisible={setAddMeetingVisible}
        onConfirm={handleAddMeetingConfirm}
        onCancel={handleAddMeetingCancel}
        milestoneCategory={milestoneCategory}
        milestone={activeMilestone}
        activeMilestone={activeMilestone?.milestone}
        milestoneMap={milestoneMap}
        isAdd={isAdd}
        projectDetailData={projectDetailData}
        onRefreshMilestones={onRefreshMilestones}
      />
      {/* Milestone Edit GR 编辑弹窗 */}
      <UpdateGRModal
        ref={updateGRModal}
        activeMilestone={activeMilestone?.milestone}
        // 注意这里是当前正在编辑中的milestone的date，不是project的currentMilestone的date
        currentMilestoneDate={activeMilestone?.date}
        separatedMilestones={separatedMilestones}
        milestone={activeMilestone}
        visible={updateGRVisible}
        isUseSaveDatas={isUseSaveDatas}
        milestones={milestones}
        milestoneCategory={milestoneCategory}
        setVisible={setUpdateGRVisible}
        onConfirm={handleUpdateGRConfirm}
        onCancel={handleUpdateGRCancel}
        actionItemCategory={actionItemCategory}
      />

      {/* 咨询自动保存弹窗 */}
      <AutoSaveModal
        visible={autoSaveVisible}
        onConfirm={handleAutoSaveConfirm}
        onClose={handleAutoSaveCancel}
      />
    </div>
  );
};

export default Milestone;
